import { createWithEqualityFn } from 'zustand/traditional';
import { persistForUser } from '@erikdakoda/ui/shared/persistForUser';
import type { StoreApi } from 'zustand';
import type { UseBoundStoreWithEqualityFn } from 'zustand/traditional';

import type { LabelKind } from '@erikdakoda/label/getLabelKind';

type LabelCollapseState = {
  /**
   * Section ids whose open state the user flipped away from {@link defaultOpenByKind}.
   * Empty means every section uses its kind default (closed).
   */
  toggledSectionIds: string[];
};

type LabelCollapseAction = {
  toggleSection: (sectionId: string) => void;
  /** Ensures the section is expanded (no-op when already open). */
  openSection: (sectionId: string, kind: LabelKind) => void;
};

export type LabelCollapseStore = LabelCollapseState & LabelCollapseAction;

export type LabelCollapseStoreHook = UseBoundStoreWithEqualityFn<StoreApi<LabelCollapseStore>>;

/**
 * Initial expanded state per label kind. All kinds start closed; opening a section adds its id to
 * the persisted toggled set.
 */
export const defaultOpenByKind: Record<LabelKind, boolean> = {
  auto: false,
  main: false,
  secondary: false,
};

/**
 * Whether a sidebar label section renders expanded.
 * @param sectionId Persisted section key (usually the Label row id).
 * @param kind Selects the open-by-default value from {@link defaultOpenByKind}.
 * @param toggledSectionIds Ids the user has flipped away from the kind default.
 * @returns True when the section should be open.
 */
export function isLabelSectionOpen(
  sectionId: string,
  kind: LabelKind,
  toggledSectionIds: readonly string[],
): boolean {
  return defaultOpenByKind[kind] !== toggledSectionIds.includes(sectionId);
}

const stores = new Map<string, LabelCollapseStoreHook>();

function createLabelCollapseStore(listId: string): LabelCollapseStoreHook {
  return createWithEqualityFn<LabelCollapseStore>()(
    persistForUser(
      (set) => ({
        toggledSectionIds: [],

        toggleSection: (sectionId) =>
          set((state) => ({
            toggledSectionIds: state.toggledSectionIds.includes(sectionId)
              ? state.toggledSectionIds.filter((id) => id !== sectionId)
              : [...state.toggledSectionIds, sectionId],
          })),

        openSection: (sectionId, kind) =>
          set((state) => {
            const wantInList = !defaultOpenByKind[kind];
            const inList = state.toggledSectionIds.includes(sectionId);
            if (inList === wantInList) {
              return state;
            }
            return {
              toggledSectionIds: wantInList
                ? [...state.toggledSectionIds, sectionId]
                : state.toggledSectionIds.filter((id) => id !== sectionId),
            };
          }),
      }),
      {
        name: `label-collapse-${listId}`,
        version: 1,
        // v0 stored collapsed ids while defaults were open; drop them so sections start closed.
        migrate: () => ({ toggledSectionIds: [] }),
        partialize: (state) => ({ toggledSectionIds: state.toggledSectionIds }),
      },
    ),
  );
}

/**
 * Returns the persisted collapse store for a sidebar list, creating it on first use.
 * One localStorage key (`label-collapse-&lt;listId>`) holds the whole list's toggled section ids.
 * @param listId Sidebar list id (`bots`, `prompts`, `metrics`, …).
 * @returns The zustand store hook for that list.
 */
export function getLabelCollapseStore(listId: string): LabelCollapseStoreHook {
  const existing = stores.get(listId);
  if (existing) {
    return existing;
  }
  const store = createLabelCollapseStore(listId);
  stores.set(listId, store);
  return store;
}

export type LabelCollapseApi = {
  toggledSectionIds: readonly string[];
  toggleSection: (sectionId: string) => void;
  openSection: (sectionId: string, kind: LabelKind) => void;
};

/**
 * Subscribes a component to a list's collapse state.
 * @param listId Sidebar list id; must be stable for the lifetime of the component.
 * @returns The toggled section ids and expand/collapse actions.
 */
export function useLabelCollapse(listId: string): LabelCollapseApi {
  const useStore = getLabelCollapseStore(listId);
  const toggledSectionIds = useStore((state) => state.toggledSectionIds);
  const toggleSection = useStore((state) => state.toggleSection);
  const openSection = useStore((state) => state.openSection);
  return { toggledSectionIds, toggleSection, openSection };
}
